class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.resultIdPrefix = this.input.id || 'search-' + Array.from(this.allPredictiveSearchInstances).indexOf(this);

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.predictiveSearchResults.querySelector('[role="listbox"]')?.remove();
      this.removeAttribute('results');
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'Escape':
        this.close();
        break;
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (previousTerm && currentButtonText.split(previousTerm).length > 2) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    if (!allVisibleElements.length) return;
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  async getSearchResults(searchTerm) {
    this.abortController.abort();
    this.abortController = new AbortController();
    const signal = this.abortController.signal;
    const requestTerm = searchTerm;
    this.setLiveRegionLoadingState();
    const searchDeferred = this.dispatchSearchUpdateEvent(searchTerm);
    const root = window.Shopify.routes.root;
    const contentUrl = new URL(root + 'search', window.location.origin);
    contentUrl.search = new URLSearchParams({ q: searchTerm, type: 'article,page', section_id: 'content-search' });
    const productUrl = new URL(routes.predictive_search_url, window.location.origin);
    productUrl.search = new URLSearchParams({ q: searchTerm, 'resources[type]': 'product', section_id: 'predictive-search' });
    const zh = document.documentElement.lang.startsWith('zh');
    try {
      const responses = await Promise.allSettled([
        fetch(productUrl, { signal }).then(async response => {
          if (!response.ok) throw new Error('Product suggestions unavailable');
          return response.text();
        }),
        fetch(contentUrl, { signal }).then(async response => {
          if (!response.ok) throw new Error('Content suggestions unavailable');
          return response.text();
        }),
      ]);
      if (signal.aborted || requestTerm !== this.getQuery()) return;
      if (responses[0].status !== 'fulfilled') throw new Error('Search suggestions unavailable');
      const doc = new DOMParser().parseFromString(responses[0].value, 'text/html');
      const result = doc.querySelector('#predictive-search-results');
      if (!result) throw new Error('Missing product suggestions');
      const target = result.querySelector('[data-content-suggestions]');
      const countNode = result.querySelector('[data-total-results]');
      let contentCount = 0;
      if (responses[1].status === 'fulfilled') {
        const contentDoc = new DOMParser().parseFromString(responses[1].value, 'text/html');
        const contentResult = contentDoc.querySelector('[data-content-search-results]');
        const heading = doc.createElement('h2');
        heading.className = 'predictive-search__heading text-body caption-with-letter-spacing';
        heading.textContent = zh ? '文章和页面' : 'Articles & pages';
        target.append(heading);
        const list = doc.createElement('ul');
        list.className = 'predictive-search__results-list list-unstyled';
        list.setAttribute('role', 'group');
        for (const row of Array.from(contentDoc.querySelectorAll('[data-content-search-item]')).slice(0, 5)) {
          const li = doc.importNode(row, true);
          li.id = 'predictive-content-' + contentCount++;
          li.setAttribute('role', 'option');
          li.setAttribute('aria-selected', 'false');
          li.className = 'predictive-search__list-item';
          li.querySelector('a').className = 'predictive-search__item link link--text';
          li.querySelector('a').tabIndex = -1;
          list.append(li);
        }
        target.append(list);
        const capacity = contentDoc.querySelector('[data-content-capacity-exceeded]');
        if (capacity) target.append(doc.importNode(capacity, true));
        else if (!contentResult) throw new Error('Missing content results');
      } else {
        const notice = doc.createElement('p');
        notice.textContent = zh ? '暂时无法加载文章建议。您仍可搜索文章和页面。' : 'Article suggestions could not load. You can still search articles and pages.';
        target.append(notice);
      }
      const allContent = doc.createElement('li');
      allContent.id = 'predictive-content-all';
      allContent.setAttribute('role', 'option');
      allContent.setAttribute('aria-selected', 'false');
      const contentLink = doc.createElement('a');
      const allUrl = new URL(contentUrl);
      allUrl.searchParams.delete('section_id');
      contentLink.href = allUrl.pathname + allUrl.search;
      contentLink.textContent = zh ? '搜索所有文章和页面' : 'Search all articles and pages';
      contentLink.className = 'predictive-search__item link link--text';
      contentLink.tabIndex = -1;
      allContent.append(contentLink);
      const allList = doc.createElement('ul');
      allList.className = 'list-unstyled';
      allList.setAttribute('role', 'group');
      allList.append(allContent);
      target.append(allList);
      const total = Number(countNode.dataset.totalResults) + contentCount;
      countNode.dataset.totalResults = total;
      countNode.textContent = zh ? total + ' 条商品和内容建议' : total + ' product and content suggestions';
      this.renderSearchResults(result.outerHTML);
      searchDeferred?.resolve({ totalCount: total });
    } catch (error) {
      if (signal.aborted) return;
      this.close();
      this.setLiveRegionText(zh ? '暂时无法加载搜索建议。按回车键搜索。' : 'Search suggestions could not load. Press Enter to search.');
      searchDeferred?.reject(error);
    }
  }

  getTotalResultCount() {
    return parseInt(this.predictiveSearchResults.querySelector('[data-total-results]')?.dataset.totalResults) || 0;
  }

  dispatchSearchUpdateEvent(query) {
    const { SearchUpdateEvent } = window.StandardEvents || {};
    if (!SearchUpdateEvent) return null;

    const deferred = SearchUpdateEvent.createPromise();
    this.dispatchEvent(
      new SearchUpdateEvent({
        search: { query },
        promise: deferred.promise,
      })
    );
    return deferred;
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.predictiveSearchResults.querySelectorAll('[id]').forEach(element => {
      element.id = this.resultIdPrefix + '-' + element.id;
    });
    this.predictiveSearchResults.querySelectorAll('[aria-labelledby], [aria-describedby], [for]').forEach(element => {
      for (const attribute of ['aria-labelledby', 'aria-describedby', 'for']) {
        if (element.hasAttribute(attribute)) {
          element.setAttribute(attribute, element.getAttribute(attribute).split(/\s+/).map(id => this.resultIdPrefix + '-' + id).join(' '));
        }
      }
    });
    this.predictiveSearchResults.querySelectorAll('a[href^="#"]').forEach(element => {
      element.setAttribute('href', '#' + this.resultIdPrefix + '-' + element.getAttribute('href').slice(1));
    });
    const listbox = this.predictiveSearchResults.querySelector('[role="listbox"]');
    if (listbox) {
      this.input.setAttribute('aria-controls', listbox.id);
      this.input.setAttribute('aria-owns', listbox.id);
    }
    this.input.removeAttribute('aria-activedescendant');
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
